<template>
  <div class="course-list">
    <!-- 顶部筛选区 -->
    <div class="filter-section">
      <el-form :inline="true" :model="filters" class="filter-form">
        <el-form-item label="课程分类" class="filter-item">
          <el-select
            v-model="filters.categoryId"
            placeholder="全部分类"
            clearable
            @change="handleFilterChange"
            class="filter-select"
          >
            <!-- 顶级分类 -->
            <el-option
              v-for="category in topLevelCategories"
              :key="category.id"
              :label="category.name"
              :value="category.id"
            />
            <!-- 二级分类（带缩进） -->
            <el-option
              v-for="category in secondLevelCategories"
              :key="category.id"
              :label="`  ${category.name}`"
              :value="category.id"
            />
          </el-select>
        </el-form-item>
        
        <el-form-item label="关键词" class="filter-item">
          <el-input
            v-model="filters.keyword"
            placeholder="搜索课程名称"
            clearable
            @clear="handleFilterChange"
            class="filter-input"
          >
            <template #append>
              <el-button @click="handleFilterChange">
                <el-icon><Search /></el-icon>
              </el-button>
            </template>
          </el-input>
        </el-form-item>
      </el-form>
    </div>

    <!-- 课程列表 -->
    <div class="course-grid">
      <el-row :gutter="20">
        <el-col
          v-for="course in courses"
          :key="course.id"
          :xs="24"
          :sm="12"
          :md="8"
          :lg="6"
        >
          <div class="course-card">
            <div class="course-cover" @click="goToCourseDetail(course.id)">
              <el-image
                :src="course.course_imgs || '/static/images/default-course.png'"
                fit="cover"
              >
                <template #error>
                  <div class="image-slot">
                    <el-icon><Picture /></el-icon>
                  </div>
                </template>
              </el-image>
            </div>
            
            <div class="course-info">
              <h3 class="course-title" @click="goToCourseDetail(course.id)">
                {{ course.course_name }}
              </h3>
              
              <div class="course-meta">
                <span class="category">{{ course.category?.name || '未分类' }}</span>
                <span class="duration">{{ course.class_hour }}课时</span>
              </div>
              
              <div class="course-footer">
                <span class="price">¥{{ course.price }}</span>
                <el-button 
                  type="primary" 
                  size="small"
                  @click.stop="handlePurchase(course.id)"
                >
                  立即购买
                </el-button>
              </div>
            </div>
          </div>
        </el-col>
      </el-row>

      <!-- 空状态 -->
      <el-empty v-if="!loading && courses.length === 0" description="暂无课程" />
    </div>

    <!-- 分页 -->
    <div class="pagination-wrapper" v-if="total > 0">
      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.pageSize"
        :total="total"
        :page-sizes="[12, 24, 36, 48]"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </div>

    <!-- 订单确认弹窗 -->
    <el-dialog
      v-model="confirmDialogVisible"
      width="480px"
      :close-on-click-modal="false"
      :show-close="false"
      custom-class="confirm-dialog"
    >
      <template #header>
        <div class="confirm-header">
          <div class="confirm-icon-wrapper">
            <el-icon :size="28" color="#fff"><Wallet /></el-icon>
          </div>
          <h3 class="confirm-title">确认订单信息</h3>
          <p class="confirm-subtitle">请核对以下课程信息，确认无误后进行支付</p>
        </div>
      </template>

      <div class="confirm-body" v-if="currentCourse">
        <div class="confirm-card">
          <div class="confirm-row">
            <span class="confirm-label">
              <el-icon><Notebook /></el-icon>课程名称
            </span>
            <span class="confirm-value">{{ currentCourse.course_name }}</span>
          </div>
          <div class="confirm-row">
            <span class="confirm-label">
              <el-icon><Clock /></el-icon>课时
            </span>
            <span class="confirm-value">{{ currentCourse.class_hour }}课时</span>
          </div>
          <div class="confirm-row">
            <span class="confirm-label">
              <el-icon><Collection /></el-icon>分类
            </span>
            <span class="confirm-value">{{ currentCourse.category?.name || '未分类' }}</span>
          </div>
        </div>

        <div class="confirm-amount-card">
          <span class="amount-label">应付金额</span>
          <div class="amount-value">
            <span class="amount-symbol">¥</span>
            <span class="amount-number">{{ currentCourse.price }}</span>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="confirm-footer">
          <el-button @click="confirmDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="goToPayment">
            <el-icon style="margin-right: 6px"><CreditCard /></el-icon>去支付
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 支付方式选择弹窗 -->
    <el-dialog
      v-model="paymentDialogVisible"
      title="选择支付方式"
      :width="selectedPaymentMethod === 'wechat' ? '700px' : '400px'"
      :close-on-click-modal="false"
    >
      <div class="payment-container" :class="{ 'wechat-active': selectedPaymentMethod === 'wechat' }">
        <div class="payment-methods">
          <div
            class="payment-item"
            :class="{ active: selectedPaymentMethod === 'wechat' }"
            @click="selectPaymentMethod('wechat')"
          >
            <img src="@/assets/images/weixin.png" alt="微信" class="payment-icon" />
            <span class="payment-name">微信支付</span>
            <i class="el-icon-check check-icon" v-if="selectedPaymentMethod === 'wechat'"></i>
          </div>
          <div
            class="payment-item"
            :class="{ active: selectedPaymentMethod === 'alipay' }"
            @click="selectPaymentMethod('alipay')"
          >
            <img src="@/assets/images/alipay.png" alt="支付宝" class="payment-icon" />
            <span class="payment-name">支付宝支付</span>
            <i class="el-icon-check check-icon" v-if="selectedPaymentMethod === 'alipay'"></i>
          </div>
          <div class="alipay-tip" v-if="selectedPaymentMethod === 'alipay'">
            <el-icon><InfoFilled /></el-icon>
            <span>点击下方按钮将跳转至支付宝收银台完成支付</span>
          </div>
        </div>

        <div class="qrcode-section" v-if="selectedPaymentMethod === 'wechat'">
          <div class="qrcode-header">
            <img src="@/assets/images/weixin.png" alt="微信" class="payment-logo" />
            <span class="payment-title">微信扫码支付</span>
          </div>
          <div class="qrcode-wrapper">
            <div v-show="qrcodeUrl" ref="qrcodeRef" class="qrcode-canvas"></div>
            <div v-show="!qrcodeUrl" class="qrcode-loading">
              <i class="el-icon-loading"></i>
              <p>二维码生成中...</p>
            </div>
          </div>
          <div class="qrcode-tips">
            <p>请使用微信扫描二维码完成支付</p>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="paymentDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmPayment" :disabled="!selectedPaymentMethod">
            {{ selectedPaymentMethod === 'alipay' ? '前往支付宝支付' : '已完成支付' }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { Search, Picture, Wallet, Notebook, Clock, Collection, CreditCard, InfoFilled } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import courseApi from '@/api/course'
import QRCode from 'qrcodejs2-fix'

const store = useStore()
const router = useRouter()

// 响应式数据
const filters = reactive({
  categoryId: null,
  keyword: ''
})

const pagination = reactive({
  page: 1,
  pageSize: 12
})

const loading = ref(false)
const confirmDialogVisible = ref(false)
const paymentDialogVisible = ref(false)
const selectedPaymentMethod = ref(null) // 不设置默认支付方式
const qrcodeUrl = ref('') // 支付二维码URL
const currentCourseId = ref(null)
const currentCourse = ref(null) // 当前选中的课程信息
const qrcodeRef = ref(null) // 二维码容器ref

// 计算属性
const courses = computed(() => store.state.course.courseList)
const categories = computed(() => store.state.course.categoryList)
const coursePagination = computed(() => store.state.course.coursePagination)
const topLevelCategories = computed(() => 
  categories.value.filter(cat => cat.level === 1).sort((a, b) => b.sort - a.sort)
)
const secondLevelCategories = computed(() => 
  categories.value.filter(cat => cat.level === 2).sort((a, b) => b.sort - a.sort)
)
const total = computed(() => coursePagination.value.total)

/**
 * 加载分类
 */
const loadCategories = async () => {
  try {
    await store.dispatch('course/fetchCategoryList')
  } catch (error) {
    console.error('加载分类失败:', error)
  }
}

/**
 * 加载课程列表
 */
const loadCourses = async () => {
  loading.value = true
  
  try {
    const params = {
      page: pagination.page,
      page_size: pagination.pageSize
    }
    
    // 只有当 categoryId 有值时才传递
    if (filters.categoryId) {
      params.category_id = filters.categoryId
    }
    
    // 只有当 keyword 有值时才传递
    if (filters.keyword) {
      params.keyword = filters.keyword
    }
    
    await store.dispatch('course/fetchCourseList', params)
  } catch (error) {
    console.error('加载课程失败:', error)
  } finally {
    loading.value = false
  }
}

/**
 * 筛选条件变化
 */
const handleFilterChange = () => {
  pagination.page = 1
  loadCourses()
}

/**
 * 页码变化
 */
const handlePageChange = (page) => {
  pagination.page = page
  loadCourses()
}

/**
 * 每页数量变化
 */
const handleSizeChange = (size) => {
  pagination.pageSize = size
  pagination.page = 1
  loadCourses()
}

/**
 * 跳转到课程详情
 */
const goToCourseDetail = (courseId) => {
  router.push(`/course/detail/${courseId}`)
}

/**
 * 购买课程 - 先弹出订单确认弹窗
 */
const handlePurchase = (courseId) => {
  currentCourseId.value = courseId
  currentCourse.value = courses.value.find(c => c.id === courseId) || null
  confirmDialogVisible.value = true
}

/**
 * 去支付 - 关闭确认弹窗，打开支付方式弹窗
 */
const goToPayment = () => {
  confirmDialogVisible.value = false
  paymentDialogVisible.value = true
  selectedPaymentMethod.value = null
  qrcodeUrl.value = ''
}

/**
 * 选择支付方式
 */
const selectPaymentMethod = async (method) => {
  selectedPaymentMethod.value = method
  // 微信需要立即生成二维码，支付宝在点击按钮时再创建订单
  if (method === 'wechat') {
    await createPaymentOrder(method)
  }
}

/**
 * 创建支付订单
 */
const createPaymentOrder = async (method) => {
  try {
    qrcodeUrl.value = '' // 清空二维码
    
    const params = {
      course_ids: [currentCourseId.value],
      payment_type: method,
      payment_method: method === 'alipay' ? 'page' : 'native',
      return_url: 'https://sdxlxh.cn/payment-successful'
    }

    const res = await courseApi.purchaseCourse(params)
    
    if (res && res.code === 2000) {
      if (method === 'alipay') {
        // 支付宝：打开新窗口跳转到支付页面
        const payData = res.data.payment?.payData
        if (payData) {
          window.open(payData, '_blank')
          paymentDialogVisible.value = false // 关闭弹窗
        } else {
          ElMessage.error('获取支付链接失败')
        }
      } else {
        // 微信：使用 payData.code_url 生成二维码
        const codeUrl = res.data.payment?.payData?.code_url
        if (codeUrl) {
          qrcodeUrl.value = codeUrl
          nextTick(() => {
            generateQRCode(codeUrl)
          })
        } else {
          ElMessage.error('获取支付二维码失败')
        }
      }
    } else {
      ElMessage.error(res.msg || '创建支付订单失败')
    }
  } catch (error) {
    console.error('创建支付订单失败:', error)
    ElMessage.error('创建支付订单失败，请稍后重试')
  }
}

/**
 * 生成二维码
 */
const generateQRCode = (url) => {
  const container = qrcodeRef.value
  if (!container) return
  // 清空旧二维码
  container.innerHTML = ''
  new QRCode(container, {
    text: url,
    width: 200,
    height: 200,
    colorDark: '#000000',
    colorLight: '#ffffff',
    correctLevel: QRCode.CorrectLevel.H
  })
}

/**
 * 确认支付
 */
const confirmPayment = async () => {
  if (!selectedPaymentMethod.value) {
    ElMessage.warning('请选择支付方式')
    return
  }

  // 支付宝：点击按钮时创建订单并跳转
  if (selectedPaymentMethod.value === 'alipay') {
    await createPaymentOrder('alipay')
    return
  }
  
  // 微信：已完成支付
  paymentDialogVisible.value = false
  ElMessage.success('支付确认成功，请稍后查看订单状态')
}

// 生命周期
onMounted(() => {
  loadCategories()
  loadCourses()
})
</script>

<style lang="scss" scoped>
.course-list {
  min-height: 400px;
  padding: 30px;
  
  .filter-section {
    margin-bottom: 24px;
    padding: 24px;
    background-color: #f8f9fa;
    border-radius: 8px;
    
    .filter-form {
      .filter-item {
        margin-right: 20px;
        margin-bottom: 0;
      }
      
      .filter-select {
        width: 200px;
      }
      
      .filter-input {
        width: 300px;
      }
    }
  }
  
  .course-grid {
    min-height: 400px;
    
    .course-card {
      margin-bottom: 20px;
      background-color: #fff;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      overflow: hidden;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
      }
      
      .course-cover {
        height: 180px;
        overflow: hidden;
        cursor: pointer;
        
        .el-image {
          width: 100%;
          height: 100%;
        }
        
        .image-slot {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          background-color: #f5f7fa;
          color: #909399;
          font-size: 30px;
        }
      }
      
      .course-info {
        padding: 20px;
        
        .course-title {
          margin: 0 0 12px 0;
          font-size: 16px;
          font-weight: 500;
          color: #333;
          cursor: pointer;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          transition: color 0.3s ease;
          
          &:hover {
            color: #4facfe;
          }
        }
        
        .course-meta {
          display: flex;
          justify-content: space-between;
          margin-bottom: 16px;
          font-size: 14px;
          color: #666;
          
          .category {
            color: #4facfe;
            font-weight: 500;
          }
        }
        
        .course-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          
          .price {
            font-size: 20px;
            font-weight: bold;
            color: #ff6b6b;
          }
          
          .el-button {
            padding: 8px 16px;
          }
        }
      }
    }
  }
  
  .pagination-wrapper {
    display: flex;
    justify-content: center;
    margin-top: 40px;
    padding: 20px 0;
  }
}

@media (max-width: 768px) {
  .course-list {
    padding: 10px;
    
    .filter-section {
      padding: 15px;
      
      .filter-form {
        .el-form {
          display: block;
        }
        
        .filter-item {
          display: block;
          margin-right: 0;
          margin-bottom: 15px;
          
          &:last-child {
            margin-bottom: 0;
          }
        }
        
        .filter-select,
        .filter-input {
          width: 100%;
        }
      }
    }
  }
}

.payment-container {
  min-height: 200px;

  &.wechat-active {
    display: flex;
    gap: 30px;
    min-height: 350px;
  }
}

.payment-methods {
  display: flex;
  flex-direction: column;
  gap: 15px;

  .wechat-active & {
    flex: 0 0 200px;
  }

  .payment-item {
    display: flex;
    align-items: center;
    padding: 15px;
    border: 2px solid #e4e7ed;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s;
    position: relative;

    &:hover {
      border-color: #409eff;
      background-color: #f0f9ff;
    }

    &.active {
      border-color: #409eff;
      background-color: #ecf5ff;
    }

    .payment-icon {
      width: 32px;
      height: 32px;
      margin-right: 12px;
      object-fit: contain;
    }

    .payment-name {
      font-size: 15px;
      color: #303133;
      font-weight: 500;
      flex: 1;
    }

    .check-icon {
      font-size: 18px;
      color: #409eff;
    }
  }

  .alipay-tip {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    padding: 12px;
    background: #fdf6ec;
    border: 1px solid #faecd8;
    border-radius: 6px;
    font-size: 13px;
    color: #e6a23c;
    line-height: 1.5;

    .el-icon {
      margin-top: 2px;
      flex-shrink: 0;
    }
  }
}

.qrcode-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 30px;

  .qrcode-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;

    .payment-logo {
      width: 28px;
      height: 28px;
      margin-right: 10px;
    }

    .payment-title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
    }
  }

  .qrcode-wrapper {
    width: 220px;
    height: 220px;
    background-color: #fff;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    padding: 10px;

    .qrcode-canvas {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .qrcode-loading {
      text-align: center;
      color: #909399;

      i {
        font-size: 32px;
        margin-bottom: 10px;
      }

      p {
        margin: 0;
        font-size: 14px;
      }
    }
  }

  .qrcode-tips {
    text-align: center;

    p {
      margin: 0;
      color: #606266;
      font-size: 14px;
      line-height: 1.6;
    }
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

@media (max-width: 768px) {
  .payment-container {
    &.wechat-active {
      flex-direction: column;
      gap: 20px;
    }
  }

  .payment-methods {
    flex: none;
  }

  .qrcode-section {
    padding: 20px;

    .qrcode-wrapper {
      width: 180px;
      height: 180px;
    }
  }
}

.confirm-header {
  text-align: center;
  padding-bottom: 8px;

  .confirm-icon-wrapper {
    width: 56px;
    height: 56px;
    margin: 0 auto 16px;
    background: linear-gradient(135deg, #409eff, #337ecc);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 16px rgba(64, 158, 255, 0.3);
  }

  .confirm-title {
    margin: 0 0 6px;
    font-size: 20px;
    font-weight: 600;
    color: #303133;
  }

  .confirm-subtitle {
    margin: 0;
    font-size: 13px;
    color: #a8abb2;
  }
}

.confirm-body {
  padding: 0 4px;

  .confirm-card {
    background: #f8f9fb;
    border-radius: 10px;
    padding: 6px 20px;
    margin-bottom: 16px;

    .confirm-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 14px 0;

      & + .confirm-row {
        border-top: 1px dashed #e8e8e8;
      }

      .confirm-label {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #606266;
        font-size: 14px;

        .el-icon {
          color: #909399;
          font-size: 16px;
        }
      }

      .confirm-value {
        color: #303133;
        font-size: 15px;
        font-weight: 500;
      }
    }
  }

  .confirm-amount-card {
    background: linear-gradient(135deg, #fff7f0, #fff0f0);
    border: 1px solid #fde2ce;
    border-radius: 10px;
    padding: 20px;
    text-align: center;

    .amount-label {
      font-size: 13px;
      color: #909399;
      display: block;
      margin-bottom: 8px;
    }

    .amount-value {
      display: flex;
      align-items: baseline;
      justify-content: center;
      color: #f56c6c;

      .amount-symbol {
        font-size: 18px;
        font-weight: 600;
        margin-right: 2px;
      }

      .amount-number {
        font-size: 36px;
        font-weight: 700;
        line-height: 1;
        font-family: 'DIN', 'Helvetica Neue', sans-serif;
      }
    }
  }
}

.confirm-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  .el-button--primary {
    padding: 10px 32px;
  }
}
</style>


<style lang="scss">
/* 确认弹窗圆角 */
.confirm-dialog {
  border-radius: 16px;
  overflow: hidden;

  .el-dialog__header {
    padding: 28px 28px 0;
    margin-right: 0;
  }

  .el-dialog__body {
    padding: 16px 28px;
  }

  .el-dialog__footer {
    padding: 12px 28px 24px;
  }
}
</style>
